import { useState } from "react"
import { Card, CardContent, CardHeader } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { Progress } from "@/components/ui/progress"
import { GlassCard } from "./GlassCard"
import { ProgressRing } from "./ProgressRing"
import { TierBadge, TierType } from "./TierBadge"
import { useApexState } from "@/hooks/useApexState"
import { 
  X, 
  Crown, 
  Star,
  Gift,
  ShoppingBag,
  Users,
  MessageSquare,
  Calendar,
  Zap,
  CheckCircle,
  Lock,
  TrendingUp
} from "lucide-react"

interface TierBenefit {
  id: string
  title: string
  description: string
  icon: React.ElementType
  isAvailable: boolean
  isNew?: boolean
}

interface TierDetails {
  tier: TierType
  name: string
  pointsRequired: number
  color: string
  benefits: TierBenefit[]
  exclusiveFeatures: string[]
}

interface TierProgressionModalProps {
  isOpen: boolean
  onClose: () => void
}

const tierData: TierDetails[] = [
  {
    tier: 'initiate',
    name: 'Initiate',
    pointsRequired: 0,
    color: 'text-muted-foreground',
    benefits: [
      {
        id: '1',
        title: 'Community Access',
        description: 'Join the Apex community and connect with members',
        icon: Users,
        isAvailable: true
      },
      {
        id: '2',
        title: 'Movement Tracking',
        description: 'Track your daily movement and earn points',
        icon: TrendingUp,
        isAvailable: true
      },
      {
        id: '3',
        title: 'Style Challenges',
        description: 'Participate in community style challenges',
        icon: Star,
        isAvailable: true
      }
    ],
    exclusiveFeatures: [
      'Basic community features',
      'Movement point earning',
      'Challenge participation'
    ]
  },
  {
    tier: 'creator',
    name: 'Creator',
    pointsRequired: 1000,
    color: 'text-tier-creator',
    benefits: [
      {
        id: '1',
        title: 'Monthly Exclusive Content',
        description: 'Access to monthly exclusive styling content and tutorials',
        icon: Gift,
        isAvailable: true
      },
      {
        id: '2',
        title: 'Priority Support',
        description: 'Faster response times for customer support',
        icon: MessageSquare,
        isAvailable: true
      },
      {
        id: '3',
        title: 'Early Collection Access',
        description: '24-hour early access to new collection launches',
        icon: ShoppingBag,
        isAvailable: true
      },
      {
        id: '4',
        title: 'Double Points',
        description: 'Earn 2x points on all purchases and activities',
        icon: Zap,
        isAvailable: true
      }
    ],
    exclusiveFeatures: [
      'All Initiate benefits',
      'Silver tier status badge',
      'Monthly exclusive content access',
      '2x point multiplier on activities',
      'Priority customer support',
      '24h early access to collections'
    ]
  },
  {
    tier: 'architect',
    name: 'Architect',
    pointsRequired: 5000,
    color: 'text-tier-architect',
    benefits: [
      {
        id: '1',
        title: 'Guaranteed Limited Drops',
        description: 'Guaranteed access to all limited edition releases',
        icon: Crown,
        isAvailable: true,
        isNew: true
      },
      {
        id: '2',
        title: 'Biannual Exclusive Gifts',
        description: 'Receive exclusive gifts twice per year',
        icon: Gift,
        isAvailable: true
      },
      {
        id: '3',
        title: 'Dedicated Concierge',
        description: 'Personal styling advice and product recommendations',
        icon: MessageSquare,
        isAvailable: true,
        isNew: true
      },
      {
        id: '4',
        title: 'Exclusive Events',
        description: 'Invitations to exclusive Apex events and experiences',
        icon: Calendar,
        isAvailable: true
      },
      {
        id: '5',
        title: 'Triple Points',
        description: 'Earn 3x points on all purchases and activities',
        icon: Zap,
        isAvailable: true
      },
      {
        id: '6',
        title: 'Private Shopping',
        description: 'Access to private shopping sessions and previews',
        icon: ShoppingBag,
        isAvailable: true,
        isNew: true
      }
    ],
    exclusiveFeatures: [
      'All Creator benefits',
      'Gold tier status badge',
      'Guaranteed limited drop access',
      'Biannual exclusive gifts',
      'Dedicated concierge chat',
      '3x point multiplier',
      'Exclusive event invitations',
      'Private shopping sessions',
      '10% tier discount on purchases'
    ]
  }
]

export function TierProgressionModal({ isOpen, onClose }: TierProgressionModalProps) {
  const { state } = useApexState()
  const [selectedTier, setSelectedTier] = useState<TierType>('creator')

  if (!isOpen || !state.user) return null

  const currentTierIndex = tierData.findIndex(tier => tier.tier === state.user!.tier)
  const selectedTierData = tierData.find(tier => tier.tier === selectedTier)
  const currentTierData = tierData[currentTierIndex]
  const nextTierData = tierData[currentTierIndex + 1]

  const currentProgress = state.user.points
  const nextTierPoints = nextTierData?.pointsRequired || currentTierData.pointsRequired
  const progressPercentage = Math.min((currentProgress / nextTierPoints) * 100, 100)
  const pointsToNext = Math.max(nextTierPoints - currentProgress, 0)

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm animate-fade-in">
      <div className="flex items-center justify-center min-h-screen p-4">
        <div className="w-full max-w-4xl max-h-[90vh] overflow-hidden">
          <GlassCard className="relative" blur="lg" opacity="high">
            {/* Close Button */}
            <Button
              variant="ghost"
              size="sm"
              className="absolute top-4 right-4 z-10"
              onClick={onClose}
            >
              <X size={20} />
            </Button>

            <div className="p-8">
              <div className="text-center mb-8">
                <h1 className="text-3xl font-bold text-foreground mb-2">
                  Tier Progression
                </h1>
                <p className="text-muted-foreground">
                  Unlock exclusive benefits as you engage with the Apex community
                </p>
              </div>

              {/* Current Status */}
              <div className="mb-8">
                <Card className="bg-card-gradient border-0">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between mb-6">
                      <div>
                        <div className="flex items-center gap-3 mb-2">
                          <TierBadge tier={state.user.tier} size="lg" />
                          <div>
                            <h2 className="text-xl font-bold text-foreground">
                              {currentTierData.name} Member
                            </h2>
                            <p className="text-muted-foreground">
                              {state.user.points.toLocaleString()} Apex Points
                            </p>
                          </div>
                        </div>
                      </div>
                      
                      <div className="text-center">
                        <ProgressRing
                          progress={progressPercentage}
                          size="lg"
                          color="primary"
                        >
                          <div className="text-center">
                            <div className="text-lg font-bold text-foreground">
                              {Math.round(progressPercentage)}%
                            </div>
                            <div className="text-xs text-muted-foreground">
                              Complete
                            </div>
                          </div>
                        </ProgressRing>
                      </div>
                    </div>

                    {nextTierData && (
                      <div>
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm text-muted-foreground">
                            Progress to {nextTierData.name}
                          </span>
                          <span className="text-sm font-medium text-primary">
                            {pointsToNext.toLocaleString()} points to go
                          </span>
                        </div>
                        <Progress value={progressPercentage} className="h-2" />
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>

              {/* Tier Selection */}
              <div className="flex justify-center gap-4 mb-8">
                {tierData.map((tier) => (
                  <button
                    key={tier.tier}
                    onClick={() => setSelectedTier(tier.tier)}
                    className={`
                      px-6 py-3 rounded-lg border transition-all duration-200
                      ${selectedTier === tier.tier
                        ? 'border-primary bg-primary/10 text-primary'
                        : 'border-white/20 hover:border-white/40'
                      }
                    `}
                  >
                    <TierBadge tier={tier.tier} size="sm" className="mb-1" />
                    <div className="text-xs font-medium">{tier.name}</div>
                  </button>
                ))}
              </div>

              {/* Selected Tier Details */}
              {selectedTierData && (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Benefits */}
                  <Card className="bg-card-gradient border-0">
                    <CardHeader>
                      <div className="flex items-center gap-2">
                        <Crown className="text-primary" size={20} />
                        <h3 className="text-lg font-semibold">
                          {selectedTierData.name} Benefits
                        </h3>
                      </div>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      {selectedTierData.benefits.map((benefit) => {
                        const Icon = benefit.icon
                        return (
                          <div key={benefit.id} className="flex gap-3">
                            <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                              <Icon size={18} className="text-primary" />
                            </div>
                            <div className="flex-1">
                              <div className="flex items-center gap-2">
                                <h4 className="font-medium text-foreground">
                                  {benefit.title}
                                </h4>
                                {benefit.isNew && (
                                  <Badge className="bg-accent text-accent-foreground text-xs">
                                    New
                                  </Badge>
                                )}
                              </div>
                              <p className="text-sm text-muted-foreground">
                                {benefit.description}
                              </p>
                            </div>
                            <div className="flex-shrink-0">
                              {benefit.isAvailable ? (
                                <CheckCircle size={16} className="text-accent" />
                              ) : (
                                <Lock size={16} className="text-muted-foreground" />
                              )}
                            </div>
                          </div>
                        )
                      })}
                    </CardContent>
                  </Card>

                  {/* Exclusive Features */}
                  <Card className="bg-card-gradient border-0">
                    <CardHeader>
                      <div className="flex items-center gap-2">
                        <Star className="text-accent" size={20} />
                        <h3 className="text-lg font-semibold">Exclusive Access</h3>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <ul className="space-y-3">
                        {selectedTierData.exclusiveFeatures.map((feature, index) => (
                          <li key={index} className="flex items-center gap-2 text-sm">
                            <CheckCircle size={12} className="text-accent flex-shrink-0" />
                            <span className="text-foreground">{feature}</span>
                          </li>
                        ))}
                      </ul>

                      {selectedTierData.tier !== state.user.tier && (
                        <div className="mt-6 pt-6 border-t border-white/10">
                          <div className="text-center">
                            <p className="text-sm text-muted-foreground mb-4">
                              {selectedTierData.pointsRequired - state.user.points > 0
                                ? `${(selectedTierData.pointsRequired - state.user.points).toLocaleString()} more points needed`
                                : "Requirements met!"}
                            </p>
                            <Button className="bg-primary hover:bg-primary/90 text-black font-semibold">
                              {selectedTierData.pointsRequired - state.user.points > 0
                                ? `Earn ${(selectedTierData.pointsRequired - state.user.points).toLocaleString()} Points`
                                : "Upgrade Now"}
                            </Button>
                          </div>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </div>
              )}

              {/* Quick Actions */}
              <div className="mt-8 flex justify-center gap-4">
                <Button variant="outline" onClick={onClose}>
                  Close
                </Button>
                <Button className="bg-accent hover:bg-accent/90 text-accent-foreground">
                  Explore Ways to Earn Points
                </Button>
              </div>
            </div>
          </GlassCard>
        </div>
      </div>
    </div>
  )
}